type EnrollmentProgressRecord = Record<string, unknown>;

const asRecord = (value: unknown): EnrollmentProgressRecord => (
  value && typeof value === 'object' && !Array.isArray(value)
    ? value as EnrollmentProgressRecord
    : {}
);

const readText = (value: unknown): string => String(value ?? '').trim();

const parseUnixSeconds = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Math.trunc(value);
  }
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value.trim());
    return Number.isFinite(parsed) ? Math.trunc(parsed) : null;
  }
  return null;
};

export const defaultEnrollmentProgress = {
  step1AcceptanceLetter: false,
  step2RegistrationForm: false,
  step3B6TForm: false,
  step4TuitionContract: false,
  step5Payment: false,
  step6ImmunizationRecords: false,
};

export type EnrollmentProgressStepKey = keyof typeof defaultEnrollmentProgress;
export type EnrollmentProgressOverrideMap = Record<string, Partial<Record<EnrollmentProgressStepKey, boolean>>>;

export const applyEnrollmentProgressCompletion = (
  overrides: EnrollmentProgressOverrideMap,
  childApplicationId: string,
  step: EnrollmentProgressStepKey,
): EnrollmentProgressOverrideMap => ({
  ...overrides,
  [childApplicationId]: { ...overrides[childApplicationId], [step]: true },
});

export const readEnrollmentProgressFlag = (value: unknown): boolean => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value > 0;
  const normalized = readText(value).toLowerCase();
  if (!normalized) return false;
  if (['false', '0', 'no', 'n', 'incomplete', 'pending'].includes(normalized)) return false;
  if (['true', '1', 'yes', 'y', 'complete', 'completed'].includes(normalized)) return true;
  return Boolean(value);
};

export const isEnrollmentRegistrationFormComplete = (...progressSources: unknown[]): boolean => (
  progressSources.some((source) => {
    const progress = asRecord(source);
    return (
      readEnrollmentProgressFlag(progress.step2RegistrationForm)
      || parseUnixSeconds(progress.formSubmittedAt) != null
    );
  })
);

export const buildEnrollmentProgressSnapshot = (...progressSources: unknown[]): EnrollmentProgressRecord => {
  const snapshot: EnrollmentProgressRecord = { ...defaultEnrollmentProgress };
  for (const source of progressSources) {
    Object.assign(snapshot, asRecord(source));
  }
  snapshot.step2RegistrationForm = isEnrollmentRegistrationFormComplete(...progressSources);
  return snapshot;
};

export type EnrollmentTargetLinkInput = {
  applicationId: string;
  childApplicationId: string;
};

export type EnrollmentIntakeLinkOptions = {
  editRegistration?: boolean;
};

export const buildEnrollmentIntakeLink = (target: EnrollmentTargetLinkInput, options: EnrollmentIntakeLinkOptions = {}): string => {
  const params = new URLSearchParams({
    applicationId: target.applicationId,
    childApplicationId: target.childApplicationId,
  });
  if (options.editRegistration) params.set('editRegistration', '1');
  return `/parent/portal--new/enrollment?${params.toString()}`;
};

export const buildEnrollmentChecklistLink = (target: EnrollmentTargetLinkInput): string => {
  const params = new URLSearchParams({
    applicationId: target.applicationId,
    childApplicationId: target.childApplicationId,
    step: 'checklist',
  });
  return `/parent/portal/enrollment?${params.toString()}`;
};

export const resolveEnrollmentTargetAction = (input: {
  target: EnrollmentTargetLinkInput;
  progress: unknown;
  allStepsComplete: boolean;
}): {
  href: string;
  label: 'Complete Form' | 'Review / Edit' | 'View / Edit';
  registrationFormComplete: boolean;
  statusHint: string | null;
} => {
  const registrationFormComplete = isEnrollmentRegistrationFormComplete(input.progress);
  if (input.allStepsComplete) {
    return {
      href: buildEnrollmentIntakeLink(input.target, { editRegistration: true }),
      label: 'View / Edit',
      registrationFormComplete,
      statusHint: 'Enrollment complete',
    };
  }
  if (registrationFormComplete) {
    return {
      href: buildEnrollmentIntakeLink(input.target, { editRegistration: true }),
      label: 'Review / Edit',
      registrationFormComplete,
      statusHint: 'Registration submitted - awaiting school response',
    };
  }
  return {
    href: buildEnrollmentIntakeLink(input.target),
    label: 'Complete Form',
    registrationFormComplete,
    statusHint: null,
  };
};
